import { XIcon } from '@heroicons/react/solid';
import React, { useEffect, useState } from 'react';
import { Formik, Field, Form } from 'formik';
import * as Yup from 'yup';
import DefaultInput from '@/components/Input';
import { useTranslation } from 'react-i18next';
import Spinner from '@/components/Spinner';
import { toast } from 'react-hot-toast';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/store';
import { EquipmentResponse, getEquipments } from '@/services/EquipmentService';
import { EquipmentCategory } from '@/enums/EquipmentCategory';
import Dropdown from '@/components/DropDown';
import TextArea from '@/components/TextArea';
import { useQuery } from '@tanstack/react-query';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { Currency } from '@/enums/Currency';
import { getCurrencyExchange } from '@/services/ExchangeRateService';
import moment from 'moment';
import { formatCurrency } from '@/utils/helpers';
import {
  addSubProjectEquipment,
  updateSubProjectEquipment,
} from '@/state/slices/subProjectMethodSlice';
import { uniqueId } from 'lodash';
import Checkbox from '@/components/Checkbox';

export interface EquipmentFormValues {
  id?: string;
  subProjectMethodId?: string;
  equipmentId: string;
  description: string;
  isLocated: boolean;
  category: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  comment: string;
  exchangeRate: number;
  name?: string;
  totalToCurrency?: number;
  equipmentCurrency?: Currency;
}

interface SubProjectEquipmentModalProps {
  closeModal: () => void;
  initialData?: EquipmentFormValues;
}

const validationSchema = Yup.object().shape({
  equipmentId: Yup.string().required(
    'SubProjectEquipmentModal.EquipmentIDRequired'
  ),
  isLocated: Yup.boolean().required(
    'SubProjectEquipmentModal.IsLocatedRequired'
  ),
  category: Yup.string().required('SubProjectEquipmentModal.TrendRequired'),
  quantity: Yup.number().required('SubProjectEquipmentModal.QuantityRequired'),
  unitPrice: Yup.number().required(
    'SubProjectEquipmentModal.UnitPriceRequired'
  ),
  comment: Yup.string(),
});

const SubProjectEquipmentModal: React.FC<SubProjectEquipmentModalProps> = ({
  closeModal,
  initialData,
}) => {
  const { t } = useTranslation();
  const [equipmentCurrency, setEquipmentCurrency] = useState(Currency.EUR);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  const initialValues: EquipmentFormValues = {
    id: initialData?.id ?? '',
    subProjectMethodId: initialData?.subProjectMethodId ?? '',
    equipmentId: initialData?.equipmentId ?? '',
    description: initialData?.description ?? '',
    isLocated: initialData?.isLocated ?? false,
    category: initialData?.category ?? '',
    quantity: initialData?.quantity ?? 0,
    unitPrice: initialData?.unitPrice ?? 0,
    totalPrice: initialData?.totalPrice ?? 0,
    comment: initialData?.comment ?? '',
    exchangeRate: initialData?.exchangeRate ?? 1,
  };

  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const { data, status } = useSelector(
    (state: RootState) => state.subProjectMethod!
  );

  const projectCurrency = data?.currency ?? Currency.EUR;

  const handleSave = async (values: EquipmentFormValues) => {
    setLoading(true);
    try {
      const equipment = equipments?.equipments.find(
        (x) => x.id === values.equipmentId
      );
      if (!equipment) {
        toast.error(t('SubProjectEquipmentModal.EquipmentNotFound'));
        return;
      }

      const subMeth = {
        ...values,
        id: initialData ? initialData.id : 'newM' + uniqueId(),
        totalPrice: values.unitPrice * values.quantity,
        exchangeRate: values.exchangeRate,
        name: equipment.equipmentName,
        totalToCurrency:
          (values.unitPrice * values.quantity) / values.exchangeRate,
        equipmentCurrency: equipment.currency,
      };

      if (initialData) {
        dispatch(updateSubProjectEquipment(subMeth));
      } else {
        dispatch(addSubProjectEquipment(subMeth));
      }

      toast.success(t('SubProjectEquipmentModal.SuccessMessage'));
      closeModal();
    } catch (error) {
      toast.error(t('SubProjectEquipmentModal.ErrorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const {
    isLoading,
    isError,
    data: equipments,
  } = useQuery<EquipmentResponse | undefined>(
    ['equipments'],
    () =>
      getEquipments({
        fromvalue: 0,
        takevalue: 0,
        search: '',
        category: EquipmentCategory.Equipment,
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: 6000,
      keepPreviousData: true,
    }
  );

  const { data: exchangeRates, isLoading: isExchLoading } = useQuery(
    [
      'exchangeRates',
      projectCurrency,
      moment(selectedDate).format('YYYY-MM-DD'),
    ],
    () => getCurrencyExchange(Currency[projectCurrency], selectedDate),
    {
      staleTime: 60 * (60 * 1000), // 5 mins
      cacheTime: 120 * (60 * 1000),
    }
  );

  return (
    <div
      id="popup-modal"
      tabIndex={-1}
      className="fixed left-0 right-0 top-0 z-[1055] flex h-full w-full items-center justify-center overflow-y-auto bg-gray-800 bg-opacity-75"
    >
      <div className="w-full max-w-[35rem] rounded-lg bg-white p-6 shadow-lg dark:bg-gray-700">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {initialData
              ? t('SubProjectEquipmentModal.EditEquipment')
              : t('SubProjectEquipmentModal.AddEquipment')}
          </h2>
          <button
            className="text-gray-500 hover:text-gray-700"
            onClick={closeModal}
          >
            <XIcon className="h-6 w-6" />
          </button>
        </div>
        {loading || isLoading ? (
          <Spinner />
        ) : (
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSave}
            enableReinitialize={true}
          >
            {({ handleChange, errors, touched, values, setFieldValue }) => (
              <Form>
                <div className="grid gap-2 md:grid-cols-2">
                  <Dropdown
                    id="equipmentId"
                    name="equipmentId"
                    optionValue="id"
                    optionLabel="equipmentName"
                    onChange={(e) => {
                      const eq = equipments?.equipments.find(
                        (x) => x.id === e.target.value
                      );
                      if (!eq) {
                        setFieldValue('equipmentId', -1);
                        return;
                      }
                      setEquipmentCurrency(eq.currency as Currency);
                      setFieldValue('equipmentId', e.target.value);
                      setFieldValue(
                        'exchangeRate',
                        exchangeRates[Currency[eq.currency as Currency]].value
                      );
                    }}
                    options={equipments?.equipments ?? []}
                    label={t('SubProjectEquipmentModal.EquipmentID')}
                    labelClassName="w-[50%]"
                    error={errors.equipmentId}
                    value={values.equipmentId}
                  />
                  <DefaultInput
                    name="description"
                    id="description"
                    value={values.description}
                    type="text"
                    label={t('SubProjectEquipmentModal.Description')}
                    errors={errors}
                    onChange={handleChange}
                  />

                  <DefaultInput
                    name="category"
                    id="category"
                    value={values.category}
                    type="text"
                    label={t('SubProjectEquipmentModal.Trend')}
                    errors={errors}
                    onChange={handleChange}
                  />
                  <DefaultInput
                    name="quantity"
                    id="quantity"
                    value={values.quantity}
                    type="number"
                    label={t('SubProjectEquipmentModal.Quantity')}
                    errors={errors}
                    onChange={handleChange}
                  />
                  <DefaultInput
                    name="unitPrice"
                    id="unitPrice"
                    value={values.unitPrice}
                    type="number"
                    label={t('SubProjectEquipmentModal.UnitPrice')}
                    errors={errors}
                    onChange={handleChange}
                  />
                  <DefaultInput
                    name="totalPrice"
                    id="totalPrice"
                    value={formatCurrency(
                      values.unitPrice * values.quantity,
                      equipmentCurrency
                    )}
                    type="text"
                    label={t('SubProjectEquipmentModal.TotalPrice')}
                    errors={errors}
                    disabled={true}
                    onChange={handleChange}
                  />
                  <DefaultInput
                    name="exchangeRate"
                    id="exchangeRate"
                    value={values.exchangeRate}
                    type="number"
                    label={t('SubProjectEquipmentModal.ExchangeRate')}
                    errors={errors}
                    onChange={handleChange}
                  />
                  <DefaultInput
                    name="totalToCurrency"
                    id="totalToCurrency"
                    value={formatCurrency(
                      (values.unitPrice * values.quantity) /
                        values.exchangeRate,
                      projectCurrency
                    )}
                    type="text"
                    label={t('SubProjectEquipmentModal.TotalToCurrency')}
                    errors={errors}
                    disabled={true}
                    onChange={handleChange}
                  />
                  <TextArea
                    name="comment"
                    id="comment"
                    value={values.comment}
                    label={t('SubProjectEquipmentModal.Comment')}
                    errors={errors}
                    onChange={handleChange}
                  />
                  <div className="flex items-center justify-center">
                    <Checkbox
                      name="isLocated"
                      id="isLocated"
                      checked={values.isLocated ?? false}
                      onChange={handleChange}
                      errors={errors}
                      label={t('SubProjectEquipmentModal.IsLocated')}
                    />
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    className="mb-2 mr-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-4 focus:ring-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:hover:border-gray-600 dark:hover:bg-gray-700 dark:focus:ring-gray-700"
                    onClick={closeModal}
                  >
                    {t('subProjectMaterialModel.Cancel')}
                  </button>
                  <button
                    type="submit"
                    className="mb-2 mr-2 rounded-lg border border-primary-700 px-5 py-2.5 text-center text-sm font-medium text-primary-700 hover:bg-primary-800 hover:text-white focus:outline-none focus:ring-4 focus:ring-primary-300 disabled:border-0 disabled:bg-gray-400 disabled:text-white disabled:hover:bg-none dark:border-primary-500   dark:text-white dark:hover:bg-primary-600 dark:hover:text-white dark:focus:ring-primary-800"
                  >
                    {initialData
                      ? t('SubProjectEquipmentModal.Update')
                      : t('SubProjectEquipmentModal.Add')}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </div>
  );
};

export default SubProjectEquipmentModal;
